import { inject, Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { concatMap, from, map, of, tap } from 'rxjs';
import { IItem } from 'src/app/EBMSuite/models/item.model';
import {
  ETransactionType,
  ITransactionLineItem
} from 'src/app/EBMSuite/models/transaction.model';
import { SalesService } from 'src/app/EBMSuite/services/sales/sales.service';

interface ManageLineItemInputType {
  checkedItems: IItem[];
  selectedItems: ITransactionLineItem[];
  taxableItems: ITransactionLineItem[];
  transactionType: ETransactionType;
}

@Injectable({
  providedIn: 'root'
})
export class AddLineItemUtil {
  readonly messageService = inject(MessageService);
  readonly salesService = inject(SalesService);

  onEdit(transactionId: string, input: ManageLineItemInputType) {
    const output: {
      selectedItems: ITransactionLineItem[];
      taxableItems: ITransactionLineItem[];
    } = {
      selectedItems: [],
      taxableItems: [...input.taxableItems]
    };

    // add to taxable items if not yet added
    for (const item of input.checkedItems) {
      const isTaxable = input.taxableItems.find((x) => x.item === item._id);
      if (!isTaxable) {
        output.taxableItems.push({
          name: item?.name,
          description: item?.description,
          itemPrice: item?.price,
          itemCost: item?.cost,
          quantity: 1,
          item: item?._id,
          isTaxed: true,
          taxes: item?.taxes
        });
      }
    }

    // increment quantity if processable item is already selected
    for (const index in input.selectedItems) {
      const found = input.checkedItems.find(
        (item) => input.selectedItems[+index].item === item._id
      );
      if (found) {
        input.selectedItems[index].quantity += 1;
      }
    }
    output.selectedItems = [...input.selectedItems];

    const newLineItems: ITransactionLineItem[] = [];
    for (const item of input.checkedItems) {
      const isSelected = input.selectedItems.find((x) => x.item === item._id);
      if (!isSelected) {
        newLineItems.push({
          name: item?.name,
          description: item?.description,
          itemPrice: item?.price,
          itemCost: item?.cost,
          quantity: 1,
          item: item?._id,
          isTaxed: true,
          taxes: item?.taxes
        });
      }
    }

    if (!newLineItems.length)
      return from(output.selectedItems).pipe(
        concatMap((item) =>
          this.salesService.updateTransactionLineItem({
            ...item
          })
        ),
        map(() => output)
      );

    // perform API call to add the new line items
    return from(newLineItems).pipe(
      concatMap((item) =>
        this.salesService.addTransactionLineItem({
          ...item,
          transaction: transactionId
        })
      ),
      tap({
        next: (res) => {
          output.selectedItems.push(res.result);

          const isItemTaxable = input.taxableItems.some(
            (x) => x.item === res.result.item
          );

          if (!isItemTaxable) output.taxableItems.push(res.result);
        }
      }),
      map(() => output)
    );
  }

  onCreate(input: ManageLineItemInputType) {
    const output: {
      selectedItems: ITransactionLineItem[];
      taxableItems: ITransactionLineItem[];
    } = {
      selectedItems: [],
      taxableItems: [...input.taxableItems]
    };

    // initial population of the selected items
    for (const data of input.selectedItems) {
      const notFound = input.checkedItems.find((x) => x._id !== data.item);
      if (notFound) output.selectedItems.push(data);
    }

    for (const item of input.checkedItems) {
      const isSelected = input.selectedItems.find((x) => x.item === item._id);
      // if already selected increase the quantity
      if (isSelected) {
        isSelected.quantity += 1;
        output.selectedItems.push(isSelected);

        // append if processable item is not yet selected
      } else {
        output.selectedItems.push({
          name: item?.name,
          description: item?.description,
          itemPrice: item?.price,
          itemCost: item?.cost,
          quantity: 1,
          item: item?._id,
          isTaxed: true,
          taxes: item?.taxes
        });
      }

      const isTaxable = input.taxableItems.find((x) => x.item === item._id);
      // append to taxable items if not yet added
      if (!isTaxable) {
        output.taxableItems.push({
          name: item?.name,
          description: item?.description,
          itemPrice: item?.price,
          itemCost: item?.cost,
          quantity: 1,
          item: item?._id,
          isTaxed: true,
          taxes: item?.taxes
        });
      }
    }

    return of(output);
  }
}
